import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { ChevronDown, CalendarClock, Award, FileDown, BookOpen } from 'lucide-react';
import { useYearContext } from '../components/Layout.jsx';
import { Empty } from '../components/states.jsx';
import RichText from '../lib/RichText.jsx';
import { formatDate, formatPoints, deadlineStatus } from '../lib/date.js';

function Assignment({ assignment, archived, lecture, basePath, open, onToggle }) {
  const status = deadlineStatus(assignment.deadline, { archived });
  const anchor = `ukol-${assignment.number}`;
  const bodyId = `ukol-${assignment.number}-obsah`;

  return (
    <article id={anchor} className={`assignment${open ? ' is-open' : ''}`}>
      <button type="button" className="assignment-head" onClick={onToggle} aria-expanded={open} aria-controls={bodyId}>
        <span className="assignment-num" aria-hidden="true">{assignment.number}</span>

        <span className="assignment-main">
          <span className="assignment-title" style={{ display: 'block', fontWeight: 600 }}>
            <span className="sr-only">Úkol {assignment.number}: </span>
            {assignment.title}
          </span>
          <span className="assignment-meta">
            {assignment.deadline && (
              <span><CalendarClock size={13} aria-hidden="true" />{formatDate(assignment.deadline)}</span>
            )}
            {assignment.points > 0 && (
              <span><Award size={13} aria-hidden="true" />{formatPoints(assignment.points)}</span>
            )}
            {/* v archivu by štítek jen zopakoval datum vedle sebe */}
            {assignment.deadline && !archived && (
              <span className={`chip chip-${status.tone} chip-status`}>{status.label}</span>
            )}
          </span>
        </span>

        <ChevronDown size={18} className="assignment-caret" aria-hidden="true" />
      </button>

      {open && (
        <div className="assignment-body" id={bodyId}>
          {assignment.summary && (
            <div className="prose" style={{ maxWidth: 'none' }}>
              <RichText text={assignment.summary} />
            </div>
          )}

          {assignment.sections?.map((section, index) => (
            <section className="assignment-section" key={index}>
              {section.heading && <h4>{section.heading}</h4>}
              <RichText text={section.body} />
            </section>
          ))}

          {(assignment.template || lecture) && (
            <div className="chip-row">
              {assignment.template && (
                <a href={assignment.template} download className="chip chip-accent">
                  <FileDown size={13} aria-hidden="true" /> Stáhnout šablonu
                </a>
              )}
              {lecture && (
                <Link to={`${basePath}/hodiny`} className="chip chip-quiet">
                  <BookOpen size={13} aria-hidden="true" /> Zadáno v {lecture.number}. hodině
                </Link>
              )}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export default function Assignments() {
  const { year, archived, basePath } = useYearContext();
  const { hash } = useLocation();
  const [openIds, setOpenIds] = useState([]);

  const assignments = year.assignments || [];

  // úkol, na který vede kotva (z odkazu v hodině), rozbalíme a odrolujeme
  useEffect(() => {
    const match = /^#ukol-(\d+)$/.exec(hash || '');
    if (!match) return;
    const target = assignments.find((a) => a.number === Number(match[1]));
    if (!target) return;

    setOpenIds((current) => (current.includes(target.id) ? current : [...current, target.id]));
    // počkáme na překreslení, ať se dá scrollovat na rozbalenou položku
    const timer = setTimeout(() => {
      document.getElementById(`ukol-${target.number}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 50);
    return () => clearTimeout(timer);
  }, [hash, assignments]);

  const lectureByAssignment = useMemo(() => {
    const map = new Map();
    for (const lecture of year.lectures || []) {
      if (lecture.assignmentNumber != null) map.set(lecture.assignmentNumber, lecture);
    }
    return map;
  }, [year.lectures]);

  const { active, past } = useMemo(() => {
    if (archived) return { active: [], past: assignments };
    const groups = { active: [], past: [] };
    for (const assignment of assignments) {
      groups[deadlineStatus(assignment.deadline).past ? 'past' : 'active'].push(assignment);
    }
    // nejbližší termín nahoře, úkoly bez termínu na konec
    groups.active.sort((a, b) => (a.deadline || '9999').localeCompare(b.deadline || '9999'));
    groups.past.reverse();
    return groups;
  }, [assignments, archived]);

  const toggle = (id) =>
    setOpenIds((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));

  const render = (list) => list.map((assignment) => (
    <Assignment
      key={assignment.id}
      assignment={assignment}
      archived={archived}
      basePath={basePath}
      lecture={lectureByAssignment.get(assignment.number)}
      open={openIds.includes(assignment.id)}
      onToggle={() => toggle(assignment.id)}
    />
  ));

  return (
    <>
      <div className="page-head">
        <h1>Úkoly</h1>
        <p>Klikni na úkol pro rozbalení zadání.</p>
      </div>

      {year.assignmentRules?.length > 0 && (
        <section style={{ marginBottom: '2.5rem' }}>
          <div className="section-title"><h2 style={{ fontSize: '1.05rem' }}>Pravidla</h2></div>
          <ul className="rules">
            {year.assignmentRules.map((rule, index) => <li key={index}>{rule}</li>)}
          </ul>
        </section>
      )}

      {assignments.length === 0 ? (
        <Empty title="Zatím žádné úkoly">První úkol se tu objeví, až ho zadám.</Empty>
      ) : (
        <>
          {active.length > 0 && (
            <>
              <h2 className="group-label">Aktuální</h2>
              <div className="stack">{render(active)}</div>
            </>
          )}
          {past.length > 0 && (
            <>
              <h2 className="group-label">{archived ? 'Všechny úkoly' : 'Po termínu'}</h2>
              <div className="stack">{render(past)}</div>
            </>
          )}
        </>
      )}
    </>
  );
}
